import { z } from "zod";
import { SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";

/**
 * One output column. Like the v1 csv_transform column (name + type), plus a
 * per-column `mode` switch:
 *
 *  - `ai`      → the column is produced by the AI engine. `expression` is an
 *                optional natural-language hint for that column.
 *  - `formula` → the column is computed deterministically (no AI) by evaluating
 *                the Excel `expression` row-by-row. The formula is authored as
 *                it would appear in the first data row and references input
 *                columns by spreadsheet letter (A = first input column, B =
 *                second, …), e.g. `="BUR"&D2` or
 *                `=SUBSTITUTE(SUBSTITUTE(D2," ",""),"-","")`.
 *
 * The schema is a discriminated union on `mode` so that `expression` is
 * required (and non-empty) for formula columns and optional for AI columns.
 */
const columnBase = {
  name: z.string().min(1),
  type: z.enum(["string", "number", "date", "boolean"]),
};

export const csvTransformV4ColumnSchema = z.discriminatedUnion("mode", [
  z.object({
    ...columnBase,
    mode: z.literal("ai"),
    expression: z.string().optional(),
  }),
  z.object({
    ...columnBase,
    mode: z.literal("formula"),
    expression: z
      .string()
      .min(1, "Formula columns require a non-empty expression"),
  }),
]);

export type CsvTransformV4Column = z.infer<typeof csvTransformV4ColumnSchema>;

export const csvTransformV4Step = defineStepConfig({
  type: "csv_transform_v4",
  name: "CSV/XLSX Transform (V4)",
  description:
    "CSV/XLSX transform with per-column control. Each output column is either produced by AI (free-text hint) or computed deterministically from an Excel formula. Deterministic columns are evaluated first and injected, then AI fills in the rest. Takes a tabular document and produces a CSV document.",
  category: "AI & Agents",

  inputSchema: z.object({
    inputDocument: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "Document containing the input CSV/XLSX",
    ),
  }),

  outputSchema: z.object({
    outputDocument: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "Document containing the transformed CSV",
    ),
  }),

  configSchema: z.object({
    columns: z
      .array(csvTransformV4ColumnSchema)
      .min(1)
      .describe("Output columns, in order. Each is AI- or formula-driven."),
    instructions: z
      .string()
      .optional()
      .describe("Optional freeform guidance applied to the AI-driven columns"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "15 minutes",
  },
});
